import { useEffect, useMemo, useRef, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import {
  getProfundidadeRevisao,
  listRevisaoNodos,
  localizarRevisao,
  obterAjustes,
  RAIZ_REVISAO,
  type Cenario,
} from "@/api/metas.service";
import { janelaHistorico, reaisInteiros } from "./metasFormato";
import {
  ARVORE_VAZIA,
  PROFUNDIDADE_MAXIMA,
  PROFUNDIDADE_PADRAO,
  aplicarCarga,
  caminhoDe,
  relativizarNiveis,
  incorporar,
  type Arvore,
  type Valores,
} from "./metasArvore";

/** Tamanho mínimo do termo para disparar a busca no servidor. */
const MINIMO_BUSCA = 2;

/** Espera após a última tecla antes de buscar. */
const ATRASO_BUSCA_MS = 400;

/** Quantos pedaços do carregamento até o N4 vão ao ERP ao mesmo tempo. */
const CONCORRENCIA_N4 = 3;

/** Oracle aceita até 1000 itens num IN — os códigos de ajustes salvos vão em lotes. */
const LOTE_CODIGOS = 500;

/**
 * Carrega e mantém a árvore de revisão de um cenário: a carga inicial (lojas
 * + ajustes salvos), a busca no servidor, "Exibir até" e a expansão nó a nó.
 * `niveisIniciais` são os níveis abaixo da loja já trazidos na carga inicial
 * (a visão que abre a tela precisa deles — evita uma segunda ida ao ERP logo
 * depois). `onCarregado` é chamado com os ajustes salvos assim que uma nova árvore
 * termina de carregar — quem chama decide o que fazer com eles (a tela usa
 * para inicializar o estado de edição) — e também, de imediato e com `{}`,
 * sempre que o cenário muda: sem isso, `ajustes`/`salvos` (que vivem em quem
 * chama) ficariam com os valores do cenário anterior até a carga nova
 * terminar, e "Salvar ajustes" ficaria clicável nesse meio-tempo — clicar
 * gravaria uma lista vazia e apagaria os ajustes já salvos do cenário novo,
 * já que `salvar_ajustes` substitui o conjunto inteiro.
 */
export function useArvoreMetas(
  clientId: string,
  cenario: Cenario | undefined,
  onCarregado: (valores: Valores) => void,
  niveisIniciais = 0,
) {
  const { notify } = useNotification();
  const idCalculo = cenario?.idCalculoCliente ?? null;
  const janela = useMemo(
    () => (cenario ? janelaHistorico(cenario.anoMeta, cenario.mesMeta) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cenario?.anoMeta, cenario?.mesMeta],
  );

  const [carregandoRevisao, setCarregandoRevisao] = useState(false);
  const [erroRevisao, setErroRevisao] = useState(false);
  const [arvore, setArvore] = useState<Arvore | null>(null);
  // Espelha `arvore` de forma síncrona: todo lugar que monta a próxima árvore
  // (busca, carregarFilhos, trocarProfundidade) lê daqui, não do `arvore`
  // capturado no closure — senão duas cargas em voo ao mesmo tempo (ex.:
  // expandir um nó e mudar "Exibir até" juntos) podem resolver fora de ordem
  // e uma sobrescreve a contribuição da outra por cima de uma base velha.
  const arvoreRef = useRef<Arvore | null>(null);
  const atualizarArvore = (nova: Arvore | null) => {
    arvoreRef.current = nova;
    setArvore(nova);
  };
  // Nós cujos filhos estão a caminho — mostra o spinner na linha.
  const [carregando, setCarregando] = useState<Set<string>>(new Set());
  const [carregandoProfundidade, setCarregandoProfundidade] = useState(false);
  // Cresce a cada cenário aberto: uma carga que termina depois de o cenário
  // ter mudado é descartada em vez de entrar na árvore errada.
  const versaoArvore = useRef(0);
  // Maior "Exibir até" já carregado de todas as lojas (0 = só lojas). Evita
  // repetir a consulta de níveis que a árvore já tem — cada ida ao ERP
  // recalcula histórico e resultado do motor de todos os nós pedidos.
  const nivelCarregado = useRef(0);
  // Mesmo controle, por loja: "Exibir até" com uma loja filtrada traz só a
  // subárvore dela. Vale o maior entre este e `nivelCarregado`.
  const nivelCarregadoLoja = useRef<Map<string, number>>(new Map());
  // N1 (um por loja) cujo N2–N4 já veio por inteiro — `carregarAteN4` não repete.
  const n1ate4 = useRef<Set<string>>(new Set());

  const [busca, setBusca] = useState("");
  const [buscando, setBuscando] = useState(false);
  // Última busca respondida pelo servidor: os acertos, já incorporados à
  // árvore junto com o caminho até cada um.
  const [resultadoBusca, setResultadoBusca] = useState<{ termo: string; alvos: Set<string> } | null>(null);
  // Cresce a cada vez que o efeito de busca roda: uma resposta que chega
  // depois de a busca ter mudado de novo (ex.: limpa ao entrar em "Grupo"
  // enquanto uma busca anterior ainda estava em voo) é descartada em vez de
  // sobrescrever o `resultadoBusca` já zerado.
  const versaoBusca = useRef(0);
  const [profundidade, setProfundidade] = useState(PROFUNDIDADE_PADRAO);
  // Último nível de categoria do cliente (teto do "Exibir até"). Até o ERP
  // responder — ou se não souber, ex.: módulo ainda sem a query — vale o
  // padrão.
  const [profundidadeMaxima, setProfundidadeMaxima] = useState(PROFUNDIDADE_MAXIMA);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

  useEffect(() => {
    versaoArvore.current += 1;
    nivelCarregado.current = 0;
    nivelCarregadoLoja.current = new Map();
    n1ate4.current = new Set();
    atualizarArvore(null);
    setErroRevisao(false);
    setResultadoBusca(null);
    setCarregando(new Set());
    onCarregado({});
    if (!cenario?.id || !idCalculo || !janela) return;

    const versao = versaoArvore.current;
    setCarregandoRevisao(true);

    (async () => {
      try {
        const [linhasIniciais, ajustesSalvos, niveisCategoria] = await Promise.all([
          listRevisaoNodos(clientId, idCalculo, RAIZ_REVISAO, niveisIniciais + 1, janela),
          obterAjustes(cenario.id!, clientId),
          // Dentro do `then`: nem uma falha síncrona derruba a carga — sem a
          // profundidade a tela só usa o teto padrão.
          Promise.resolve()
            .then(() => getProfundidadeRevisao(clientId))
            .catch(() => null),
        ]);
        let construida = aplicarCarga(ARVORE_VAZIA, RAIZ_REVISAO, niveisIniciais + 1, linhasIniciais);

        // Ajustes salvos vêm por código, não por id: o ERP resolve cada um
        // ao nó (e ao caminho dele) para que a loja mostre o total ajustado
        // sem a subárvore carregada. Só VENDA/MES — o grão desta tela.
        const relevantes = ajustesSalvos.filter((a) => a.indicador === "VENDA" && a.alvo === "MES");
        const valores: Valores = {};

        // revisao_localizar não devolve loja (filtra tipo_nodo <> 'EMPRESA'),
        // mas toda loja já vem na carga inicial: o ajuste de loja casa direto
        // pelo código. Sem isto ele sumia da tela ao reabrir — e o próximo
        // salvar_ajustes, que substitui o conjunto inteiro, apagava do banco.
        const lojaPorCodigo = new Map(construida.lojas.map((id) => [construida.nos[id]?.codigo, id]));
        relevantes
          .filter((a) => a.tipoNodo === "EMPRESA")
          .forEach((a) => {
            const id = lojaPorCodigo.get(a.codigoNodo);
            if (id) valores[id] = reaisInteiros(a.valorInformado);
          });

        const deNos = relevantes.filter((a) => a.tipoNodo !== "EMPRESA");
        if (deNos.length) {
          const codigos = [...new Set(deNos.map((a) => a.codigoNodo))];
          const porChave = new Map<string, { id: string; codEmpresa: number | null }[]>();
          for (let i = 0; i < codigos.length; i += LOTE_CODIGOS) {
            const linhas = await localizarRevisao(clientId, idCalculo, { codigos: codigos.slice(i, i + LOTE_CODIGOS) }, janela);
            construida = incorporar(construida, linhas);
            linhas
              .filter((linha) => linha.alvo)
              .forEach((linha) => {
                const chave = `${linha.tipoNodo}:${linha.codigo}`;
                const codEmpresa = linha.codEmpresa !== null && Number.isFinite(Number(linha.codEmpresa)) ? Number(linha.codEmpresa) : null;
                porChave.set(chave, [...(porChave.get(chave) ?? []), { id: linha.idNodo, codEmpresa }]);
              });
          }
          deNos.forEach((a) => {
            // Ajuste sem loja (salvo antes do campo existir) vale para todas as lojas.
            (porChave.get(`${a.tipoNodo}:${a.codigoNodo}`) ?? [])
              .filter((no) => a.codEmpresa == null || a.codEmpresa === no.codEmpresa)
              .forEach((no) => {
                valores[no.id] = reaisInteiros(a.valorInformado);
              });
          });
        }

        if (versao !== versaoArvore.current) return;
        atualizarArvore(construida);
        setProfundidadeMaxima(niveisCategoria ?? PROFUNDIDADE_MAXIMA);
        nivelCarregado.current = niveisIniciais;
        setExpandidos(new Set());
        setProfundidade(PROFUNDIDADE_PADRAO);
        onCarregado(valores);
      } catch {
        if (versao === versaoArvore.current) {
          setErroRevisao(true);
          notify("Não foi possível carregar a revisão deste cálculo.", "error");
        }
      } finally {
        if (versao === versaoArvore.current) setCarregandoRevisao(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cenario?.id, idCalculo, clientId]);

  // Busca no servidor, com atraso após a última tecla. O termo vai como foi
  // digitado (o ERP compara em caixa alta, sem tratar acento).
  useEffect(() => {
    versaoBusca.current += 1;
    const minhaVersaoBusca = versaoBusca.current;
    const termo = busca.trim();
    if (!arvore || !idCalculo || !janela || termo.length < MINIMO_BUSCA) {
      setResultadoBusca(null);
      setBuscando(false);
      return;
    }
    const versao = versaoArvore.current;
    const handle = setTimeout(async () => {
      setBuscando(true);
      try {
        const linhas = await localizarRevisao(clientId, idCalculo, { termo }, janela);
        if (versao !== versaoArvore.current || minhaVersaoBusca !== versaoBusca.current) return;
        const base = arvoreRef.current;
        if (base) atualizarArvore(incorporar(base, linhas));
        setResultadoBusca({ termo, alvos: new Set(linhas.filter((l) => l.alvo).map((l) => l.idNodo)) });
      } catch {
        if (versao === versaoArvore.current && minhaVersaoBusca === versaoBusca.current) {
          notify("Não foi possível buscar na árvore.", "error");
        }
      } finally {
        if (versao === versaoArvore.current && minhaVersaoBusca === versaoBusca.current) setBuscando(false);
      }
    }, ATRASO_BUSCA_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, arvore === null, idCalculo]);

  /** Traz os filhos de um nó uma única vez; quem já tem `filhos` não volta ao servidor. */
  const carregarFilhos = async (id: string) => {
    const atual = arvoreRef.current;
    if (!atual || !idCalculo || !janela || atual.filhos[id] || carregando.has(id)) return;
    const versao = versaoArvore.current;
    setCarregando((prev) => new Set(prev).add(id));
    try {
      const linhas = await listRevisaoNodos(clientId, idCalculo, id, 1, janela);
      if (versao !== versaoArvore.current) return;
      const base = arvoreRef.current;
      if (base) atualizarArvore(aplicarCarga(base, id, 1, linhas));
    } catch {
      if (versao === versaoArvore.current) {
        notify(`Não foi possível carregar os itens de ${arvoreRef.current?.nos[id]?.nome ?? "este nó"}.`, "error");
      }
    } finally {
      if (versao === versaoArvore.current) {
        setCarregando((prev) => {
          const proximo = new Set(prev);
          proximo.delete(id);
          return proximo;
        });
      }
    }
  };

  /**
   * Carrega todos os níveis até `nivel` — de todas as lojas numa chamada só
   * (as lojas são o nível 1 do ERP, daí `nivel + 1`) ou, com `lojaId`, só da
   * subárvore dessa loja — sem mexer em `profundidade` nem em `expandidos`:
   * quem chama decide o que abrir, já que "Por loja" e "Grupo" têm cada um o
   * seu "Exibir até". Devolve a árvore já com os novos níveis incorporados
   * (quem chama precisa dela na hora, sem esperar o próximo render) ou `null`
   * se a carga falhou / o cenário mudou no meio.
   */
  const carregarNiveis = async (nivel: number, lojaId?: string): Promise<Arvore | null> => {
    if (!idCalculo || !janela) return arvoreRef.current;
    const jaCarregado = Math.max(nivelCarregado.current, lojaId ? (nivelCarregadoLoja.current.get(lojaId) ?? 0) : 0);
    if (nivel <= jaCarregado && arvoreRef.current) return arvoreRef.current;
    const versao = versaoArvore.current;
    setCarregandoProfundidade(true);
    try {
      const raiz = lojaId ?? RAIZ_REVISAO;
      const niveis = lojaId ? nivel : nivel + 1;
      const linhas = await listRevisaoNodos(clientId, idCalculo, raiz, niveis, janela);
      const base = arvoreRef.current;
      if (versao !== versaoArvore.current || !base) return null;
      const novaArvore = aplicarCarga(base, raiz, niveis, linhas);
      atualizarArvore(novaArvore);
      if (lojaId) nivelCarregadoLoja.current.set(lojaId, Math.max(nivelCarregadoLoja.current.get(lojaId) ?? 0, nivel));
      else nivelCarregado.current = Math.max(nivelCarregado.current, nivel);
      return novaArvore;
    } catch {
      if (versao === versaoArvore.current) notify("Não foi possível carregar os níveis pedidos.", "error");
      return null;
    } finally {
      if (versao === versaoArvore.current) setCarregandoProfundidade(false);
    }
  };

  /**
   * Traz `niveis` níveis abaixo de `id`. Se o pedaço falha (o CloudFront
   * corta a API em 30s e o corte cancela o trabalho no ERP), quebra em
   * pedaços menores: primeiro os filhos de `id`, depois cada filho com um
   * nível a menos — até o pedido de um nível só, que não tem mais o que dividir.
   */
  const carregarSubarvore = async (id: string, niveis: number, versao: number): Promise<void> => {
    if (!idCalculo || !janela) return;
    try {
      const linhas = await listRevisaoNodos(clientId, idCalculo, id, niveis, janela);
      if (versao !== versaoArvore.current) return;
      const base = arvoreRef.current;
      if (base) atualizarArvore(aplicarCarga(base, id, niveis, relativizarNiveis(id, linhas)));
    } catch (erro) {
      if (niveis <= 1) throw erro;
      await carregarSubarvore(id, 1, versao);
      if (versao !== versaoArvore.current) return;
      for (const filho of arvoreRef.current?.filhos[id] ?? []) await carregarSubarvore(filho, niveis - 1, versao);
    }
  };

  /**
   * Carrega até o N4 das `lojasIds` em pedaços — um N1 de cada loja por vez
   * (N2–N4 abaixo dele), `CONCORRENCIA_N4` ao mesmo tempo —, em vez de uma
   * chamada só para todas as lojas, que passaria dos 30s do CloudFront.
   * `onProgresso` recebe quantos pedaços já terminaram. Pedaços que já vieram
   * numa chamada anterior não voltam ao servidor. Devolve a árvore completa
   * até o N4, ou `null` se um pedaço falhou (já avisado) ou o cenário mudou.
   */
  const carregarAteN4 = async (
    lojasIds: string[],
    onProgresso: (feitos: number, total: number) => void,
  ): Promise<Arvore | null> => {
    const base = arvoreRef.current;
    if (!base || !idCalculo || !janela) return base;
    const versao = versaoArvore.current;
    const pendentes = lojasIds.flatMap((loja) => base.filhos[loja] ?? []).filter((n1) => !n1ate4.current.has(n1));
    const total = pendentes.length;
    let feitos = 0;
    let proximo = 0;
    let falhou = false;
    onProgresso(0, total);
    const trabalhador = async () => {
      while (!falhou && proximo < pendentes.length) {
        const id = pendentes[proximo++]!;
        try {
          await carregarSubarvore(id, 3, versao);
          if (versao !== versaoArvore.current) return;
          n1ate4.current.add(id);
          onProgresso(++feitos, total);
        } catch {
          falhou = true;
          notify(`Não foi possível carregar ${arvoreRef.current?.nos[id]?.nome ?? "uma categoria"} até o N4.`, "error");
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCORRENCIA_N4, total) }, trabalhador));
    if (falhou || versao !== versaoArvore.current) return null;
    return arvoreRef.current;
  };

  /**
   * "Exibir até" de "Por loja": carrega os níveis pedidos e abre o que tem
   * algo abaixo dentro do pedido. Com `lojaId` (loja filtrada), carrega e
   * abre só essa loja — as demais nem aparecem na tela.
   */
  const trocarProfundidade = async (nivel: number, lojaId?: string): Promise<Arvore | null> => {
    setProfundidade(nivel);
    if (nivel === 0) {
      setExpandidos(new Set());
      return arvoreRef.current;
    }
    const versao = versaoArvore.current;
    const novaArvore = await carregarNiveis(nivel, lojaId);
    if (!novaArvore || versao !== versaoArvore.current) return novaArvore;
    // Abre até `nivel` inclusive (lojas são o nível 0): os nós do próprio
    // nível pedido ficam visíveis, mas fechados.
    setExpandidos(
      new Set(
        Object.values(novaArvore.nos)
          .filter((no) => no.nivel < nivel && (!lojaId || caminhoDe(novaArvore, no.id)[0] === lojaId))
          .map((no) => no.id),
      ),
    );
    return novaArvore;
  };

  const toggleNo = (id: string) => {
    const abrindo = !expandidos.has(id);
    setExpandidos((prev) => {
      const proximo = new Set(prev);
      if (!proximo.delete(id)) proximo.add(id);
      return proximo;
    });
    if (abrindo) void carregarFilhos(id);
  };

  const recolher = () => {
    setBusca("");
    setProfundidade(PROFUNDIDADE_PADRAO);
    setExpandidos(new Set());
  };

  return {
    arvore,
    carregandoRevisao,
    erroRevisao,
    carregando,
    carregandoProfundidade,
    profundidade,
    profundidadeMaxima,
    trocarProfundidade,
    carregarNiveis,
    carregarAteN4,
    busca,
    setBusca,
    buscando,
    resultadoBusca,
    expandidos,
    toggleNo,
    carregarFilhos,
    recolher,
  };
}
